// TODO: not working. not critical since not used by any brand, but must be fixed in the future.

/* global paypal */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import Skeleton from 'coreComponents/Skeleton';
import {useLocalesMgr, useNotify, useRequestClass, useUI} from 'coreHooks';
import {ExpressPaymentButtonSection} from 'coreStore/data/basket/types';
import {EAddToCartType} from 'coreStore/data/ui/types';
import {useRouterHistory as useHistory} from 'coreUtils/adapters/router';
import {loadScript} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useURLs} from 'brandHooks';

import {
	IPayPalButtonProps,
	PayPalButtonStyleColor,
	PayPalButtonStyleHeight,
	PayPalButtonStyleLabel,
	PayPalButtonStyleShape,
	PayPalButtonStyleSize,
} from './IPayPalButton';
import messages from './messages';

import styles from './PayPalButton.module.scss';

let buttonActions = null;
let selectedVariantID = null;

const PayPalButton = ({
	className,
	product,
	onClick,
	section,
	buttonStyle = {
		size: PayPalButtonStyleSize.RESPONSIVE,
		shape: PayPalButtonStyleShape.RECT,
		color: PayPalButtonStyleColor.BLACK,
		height: PayPalButtonStyleHeight.HEIGHT,
		tagline: false,
		label: PayPalButtonStyleLabel.PAYPAL,
	},
	overridePayPalButtonStyles = {}
}: IPayPalButtonProps) => {
	const {managerRepository} = useGeneralProvider();
	const sfccURLs = useURLs();
	const localeMgr = useLocalesMgr();
	const history = useHistory();
	const [apiLoaded, setApiLoaded] = useState(false);
	const ppButton = useRef();
	const {isProduction} = useRequestClass();
	const notify = useNotify();
	const intl = useIntl();
	const currentLocale = localeMgr.getCurrentLocale();
	const addProductToCart = useUI('addProductToCart');
	const sizeSelectorSelectedPID: string | null = useUI('sizeSelectorSelectedPID');

	const resetSelectedVariantId = useCallback(() => {
		selectedVariantID = null;
		managerRepository.getUiMgr().resetProductToAddToCart();
	}, []);

	useEffect(() => {
		if (!buttonActions) {
			return;
		}

		if (addProductToCart?.productId) {
			selectedVariantID = addProductToCart?.productId;
			buttonActions.enable();
		} else if (sizeSelectorSelectedPID) {
			selectedVariantID = sizeSelectorSelectedPID;
			buttonActions.enable();
		}
	}, [addProductToCart?.productId, sizeSelectorSelectedPID]);

	useEffect(() => {
		if (selectedVariantID) {
			buttonActions.disable();
		}
	}, [selectedVariantID]);

	useEffect(() => {
		loadScript('https://www.paypalobjects.com/api/checkout.js', 'paypal-api', {
			'version-4': '',
		}).then(() => {
			setApiLoaded(true);
		});
	}, []);

	useEffect(() => {
		if (apiLoaded) {
			paypal.Button.render(PayPalButtonAPI, ppButton.current);
		}
	}, [apiLoaded]);

	useEffect(() => {
		resetSelectedVariantId();
	}, [product?.id]);

	const buttonStyleConfiguration = useMemo(() => {
		return {
			...buttonStyle,
			...overridePayPalButtonStyles,
		};
	}, [overridePayPalButtonStyles]);

	const paypalLocale = useMemo(() => {
		const countryCode = currentLocale.getCountryCode();
		const languageCode = currentLocale.getLanguageCode();

		if (apiLoaded && countryCode in paypal.config.locales) {
			if (paypal.config.locales[countryCode].indexOf(languageCode) !== -1) {
				return `${languageCode}_${countryCode}`;
			} else {
				return `${countryCode}_en`;
			}
		} else {
			return localeMgr.getDefaultLocale().getISOCode('_');
		}
	}, [currentLocale.getCode()]);

	const handleClick = () => {
		if (product && typeof onClick === 'function') {
			if (!selectedVariantID) {
				onClick(EAddToCartType.PAYPAL);
			}
		}
	};

	const handlePayment = () => {
		let extraParams = {};

		if (selectedVariantID) {
			extraParams.source = 'product';
			extraParams.pid = selectedVariantID;
		}

		return paypal.request.post(sfccURLs.PAYPAL_POPUP, extraParams).then(function(res) {
			if (res.PaypalError || !res.PaypalToken) {
				notify.error(res.PaypalError || intl.formatMessage(messages.generalError));
			}

			return res.PaypalToken;
		});
	};

	const handleValidate = (actions) => {
		if (product) {
			buttonActions = actions;
			actions.disable();
		}
	};
	const PayPalButtonAPI = {
		env: isProduction ? 'production' : 'sandbox',
		commit: false,
		style: buttonStyleConfiguration,
		locale: paypalLocale,
		payment: handlePayment,
		onAuthorize: function(data, actions) {
			const redirectUrl = new URL(data.returnUrl);
			resetSelectedVariantId();

			return actions.redirect(null, `${redirectUrl.pathname}${redirectUrl.search}`);
		},
		onError: function(err) {
			notify.error(err);
			resetSelectedVariantId();

			if (section === ExpressPaymentButtonSection.MINICART) {
				history.push(sfccURLs.CART);
			}
		},
		onCancel: function() {
			resetSelectedVariantId();
		},
		onClick: handleClick,
		validate: (actions) => handleValidate(actions),
	};

	return apiLoaded ? <div ref={ppButton} className={cn('paypal-button', styles['paypal-button'], className)} /> : <Skeleton />;
};

export default PayPalButton;
